import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { Role, View } from '../constants/roles';
import { useApiToken } from '../hooks/useApiToken';
import { listMyCredentials, listProviders } from '../utils/integrationsApi';
import { isCredentialStoreUnconfigured } from '../utils/apiError';

/**
 * Honest status of the Lead Advisor's company-data enrichment key, shown on the Add Company form.
 * The provider comes from the server catalogue (`GET /me/integrations/providers`, kind
 * COMPANY_ENRICHMENT) — never hardcoded. The lookup itself happens in the project's Company
 * Onboarding step (suggestions, explicit Apply); this only says whether a key is configured and
 * links to Integrations. States: checking / no provider offered (renders nothing) / no key /
 * key stored / could-not-check.
 *
 * Plain Tailwind, no antd import: it mounts inside DashboardPage, whose Jest suite cannot
 * require() real antd under this repo's CRA config (@rc-component/picker locale resolution).
 */
const EnrichmentKeyNotice = ({ onManage }) => {
    const { user, setCurrentView } = useApp();
    const getToken = useApiToken();
    const isLeadAdvisor = user?.role === Role.LEAD_ADVISOR;
    const [state, setState] = useState({ phase: 'checking', spec: null, stored: false });

    useEffect(() => {
        if (!isLeadAdvisor) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const [providers, creds] = await Promise.all([listProviders(getToken), listMyCredentials(getToken)]);
                const spec = providers.find((p) => p.kind === 'COMPANY_ENRICHMENT') || null;
                if (!cancelled) setState({ phase: 'ready', spec, stored: !!spec && creds.some((c) => c.provider === spec.providerId) });
            } catch (err) {
                if (!cancelled) setState({ phase: 'error', spec: null, stored: false, detail: isCredentialStoreUnconfigured(err) ? err.message : null });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isLeadAdvisor, getToken]);

    if (!isLeadAdvisor) return null;

    const manage = (
        <button
            type="button"
            className="shrink-0 text-xs font-bold text-[color:var(--color-accent)] underline"
            onClick={() => {
                if (onManage && onManage() === false) return; // caller vetoed (e.g. unsaved draft)
                setCurrentView(View.INTEGRATIONS);
            }}
        >
            Open Integrations
        </button>
    );

    const box = (tone, title, body, showManage) => (
        <div role="status" className={`flex items-start justify-between gap-3 rounded-2xl border px-4 py-3 text-xs ${tone}`}>
            <div>
                <div className="font-bold">{title}</div>
                {body ? <div className="mt-1 font-medium">{body}</div> : null}
            </div>
            {showManage ? manage : null}
        </div>
    );

    if (state.phase === 'checking') {
        return box('border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] text-[color:var(--color-text-muted)]', 'Checking company data integration…', null, false);
    }
    if (state.phase === 'error') {
        return box('border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)]', 'Could not check the company data integration.', state.detail, true);
    }
    if (!state.spec) return null; // server offers no enrichment provider
    if (!state.stored) {
        return box(
            'border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)]',
            `No ${state.spec.displayName} key configured`,
            'Add your own API key in Integrations to auto-fetch company data in Company Onboarding. Suggestions are always reviewed before they are applied.',
            true,
        );
    }
    return box(
        'border-[color:var(--color-info-line)] bg-[color:var(--color-info-bg)] text-[color:var(--color-accent)]',
        `${state.spec.displayName} key is configured`,
        'After the company is created, use Auto-fetch in its Company Onboarding step. Suggestions show their source and are applied only when you choose.',
        true,
    );
};

export default EnrichmentKeyNotice;
